import uniqueId from "lodash-es/uniqueId";
import {
  ConditionEntry,
  ConditionGroupEntry,
  ConditionLeaf,
  isConditionGroup,
} from "@/components/ConditionBuilder/types";

export { isConditionGroup };

/**
 * Mints a stable unique condition id without allocating a throwaway leaf.
 * Use this (rather than `createConditionLeaf().id`) when only the id is
 * needed — e.g. a `genId` passed to a tree-mapping reader.
 */
export const newConditionId = (): string => uniqueId("cond_");

/** Creates a new empty leaf condition with a stable unique ID */
export const createConditionLeaf = (): ConditionLeaf => ({
  id: newConditionId(),
  attribute: null,
  operator: null,
  matchWith: null,
  value: null,
});

/** Creates a new nested group with one empty (null-seeded) leaf inside. */
export const createConditionGroup = (): ConditionGroupEntry => ({
  id: uniqueId("group_"),
  combinator: "OR",
  conditions: [createConditionLeaf()],
});

/**
 * Returns true when a stored condition value is a meaningful, user-entered
 * value (string, populated array, etc.). Used by row-level validators to
 * decide whether a row has been "started" so empty siblings should be flagged.
 */
export const hasNonEmptyConditionValue = (v: unknown): boolean => {
  if (v === undefined || v === null || v === "") return false;
  if (Array.isArray(v)) return v.length > 0;
  return true;
};

/**
 * Checks whether an operator *id* ("in", "not_in") requires a multi-value input.
 *
 * Keyed by operator id, which is what the enterprise/MCP condition builders
 * carry. DomainPolicy's `fnConditionBuilderIsOperationMultiType` does the same
 * job for operator *display names* ("in", "not in") — the two vocabularies are
 * different, so do not merge the lists or reuse one helper for both.
 */
export const isMultiValueOperatorId = (operatorId?: string): boolean =>
  ["in", "not_in"].includes(operatorId?.toLowerCase() ?? "");

/** Combinator options for AND/OR dropdown */
export const COMBINATOR_OPTIONS = [
  { id: "AND", name: "And" },
  { id: "OR", name: "Or" },
] as const;

export type CombinatorOption = (typeof COMBINATOR_OPTIONS)[number];

/**
 * Serializes the condition tree to a plain output format.
 * Use this to get the final output JSON from the builder's form values.
 */
export type SerializedCondition = {
  attribute: string;
  operator: string;
  /** Selected Match With option id. Present only when enableMatchWith is used. */
  matchWith?: string;
  value: string | string[];
};

export type SerializedGroup = {
  combinator: "AND" | "OR";
  conditions: (SerializedCondition | SerializedGroup)[];
};

export interface SerializeConditionTreeOptions {
  /**
   * Which attribute field names it in the output. `"name"` writes the
   * attribute's stored key (what a saved policy calls `elementName`) instead
   * of the picker's option id, which for metadata-backed attributes is an
   * opaque column ID. Defaults to `"id"`.
   */
  attributeKey?: "id" | "name";
}

export const serializeConditionTree = (
  conditions: ConditionEntry[],
  combinator: "AND" | "OR" = "OR",
  options: SerializeConditionTreeOptions = {},
): SerializedGroup => ({
  combinator,
  conditions: conditions.map(entry => {
    if (isConditionGroup(entry)) {
      return serializeConditionTree(
        entry.conditions,
        entry.combinator,
        options,
      );
    }
    const attributeLabel =
      options.attributeKey === "name"
        ? (entry.attribute?.name ?? entry.attribute?.id)
        : entry.attribute?.id;
    const leaf: SerializedCondition = {
      attribute: attributeLabel ?? "",
      operator: entry.operator?.id ?? "",
      value: Array.isArray(entry.value)
        ? entry.value.map(v => v.id)
        : (entry.value ?? ""),
    };
    // Only include matchWith when set, so consumers that don't use
    // enableMatchWith get byte-identical output.
    if (entry.matchWith?.id) {
      leaf.matchWith = entry.matchWith.id;
    }
    return leaf;
  }),
});
